import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { api } from '../api/client';
import { EmptyState, ErrorState, Health, ReachDot, SkeletonTable, useFetch } from '../components/ui';

interface VitalsRow {
  id: string;
  name: string;
  kind: string;
  health: string;
  customerId: string | null;
  siteId: string | null;
  cpu: number | null;
  memory: number | null;
  disk: number | null;
  ts: number | null;
}

/** Only the fields this page needs off `/api/customers` and `/api/sites`. */
interface NamedOption {
  id: string;
  name: string;
}

type SortKey = 'name' | 'client' | 'cpu' | 'memory' | 'disk';

/** Samples older than this are shown greyed: the collector has stopped reporting. */
const STALE_MS = 15 * 60_000;

function Cell({ value, stale }: { value: number | null; stale: boolean }) {
  if (value === null) return <span style={{ color: 'var(--itr-text-muted)' }}>—</span>;
  const color = stale ? 'var(--itr-text-muted)' : value >= 90 ? 'var(--itr-danger)' : value >= 75 ? 'var(--itr-warning)' : 'var(--itr-success)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
      <div style={{ width: 70, height: 6, borderRadius: 3, background: 'var(--itr-border)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, value)}%`, height: '100%', background: color }} />
      </div>
      <span style={{ width: 42, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{value.toFixed(0)}%</span>
    </div>
  );
}

export function MonitoringPage() {
  const { data, loading, error, reload } = useFetch(() => api.get<VitalsRow[]>('/api/monitoring/vitals'), [], { refreshMs: 30_000 });
  const [sort, setSort] = useState<SortKey>('cpu');
  const [kind, setKind] = useState('');
  const [client, setClient] = useState('');
  const { data: customers } = useFetch(() => api.get<NamedOption[]>('/api/customers'), []);
  const { data: sites } = useFetch(() => api.get<NamedOption[]>('/api/sites'), []);
  const customerName = useMemo(() => new Map((customers ?? []).map((c) => [c.id, c.name])), [customers]);
  const siteName = useMemo(() => new Map((sites ?? []).map((s) => [s.id, s.name])), [sites]);
  const clientOf = (r: VitalsRow) => (r.customerId ? customerName.get(r.customerId) ?? '' : '');
  const [onlyReporting, setOnlyReporting] = useState(true);

  const rows = data ?? [];
  const kinds = useMemo(() => [...new Set(rows.map((r) => r.kind))].sort(), [rows]);
  const shown = useMemo(() => {
    const list = rows.filter((r) => (!kind || r.kind === kind) && (!client || (client === 'internal' ? !r.customerId : r.customerId === client)) && (!onlyReporting || r.cpu !== null || r.memory !== null || r.disk !== null));
    return list.sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      // Internal resources (no client) sort last.
      if (sort === 'client') return (clientOf(a) || '\uffff').localeCompare(clientOf(b) || '\uffff') || a.name.localeCompare(b.name);
      return (b[sort] ?? -1) - (a[sort] ?? -1);
    });
  }, [rows, kind, client, onlyReporting, sort, customerName]);

  const now = Date.now();
  const avg = (key: 'cpu' | 'memory') => {
    const v = rows.map((r) => r[key]).filter((n): n is number => n !== null);
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  };
  const disks = rows.filter((r) => r.disk !== null);
  const worstDisk = disks.reduce<VitalsRow | null>((w, r) => (w === null || r.disk! > w.disk! ? r : w), null);
  const hot = rows.filter((r) => (r.cpu ?? 0) >= 90 || (r.memory ?? 0) >= 90 || (r.disk ?? 0) >= 90).length;
  const tone = (v: number | null) => (v === null ? undefined : v >= 90 ? 'var(--itr-danger)' : v >= 75 ? 'var(--itr-warning)' : undefined);
  const reporting = rows.filter((r) => r.cpu !== null || r.memory !== null || r.disk !== null).length;

  const head = (key: SortKey, label: string, right = true) => (
    <th data-align={right ? 'right' : undefined} style={{ cursor: 'pointer', width: right ? 170 : undefined }} onClick={() => setSort(key)}>
      {label}
      {sort === key ? ' ↓' : ''}
    </th>
  );

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Monitoring</h1>
          <div className="page-subtitle">
            CPU, memory and disk · {reporting} of {rows.length} resources reporting
          </div>
        </div>
      </div>

      <div className="stat-strip" style={{ marginBottom: 14 }}>
        {([['Average CPU', avg('cpu')], ['Average memory', avg('memory')]] as const).map(([label, v]) => (
          <div key={label} className="stat-chip">
            <div className="stat-chip-label">{label}</div>
            <div className="stat-chip-value" style={{ color: tone(v) }}>{v === null ? '—' : `${v.toFixed(0)}%`}</div>
            <div className="stat-chip-sub">across {reporting} resources</div>
          </div>
        ))}
        <div className="stat-chip">
          <div className="stat-chip-label">Fullest disk</div>
          <div className="stat-chip-value" style={{ color: tone(worstDisk?.disk ?? null) }}>
            {worstDisk ? `${worstDisk.disk!.toFixed(0)}%` : '—'}
          </div>
          <div className="stat-chip-sub">{worstDisk?.name ?? 'no disk data'}</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Over 90%</div>
          <div className="stat-chip-value" style={{ color: hot > 0 ? 'var(--itr-danger)' : undefined }}>{hot}</div>
          <div className="stat-chip-sub">CPU, memory or disk</div>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} style={{ width: 200 }}>
          <option value="">All kinds</option>
          {kinds.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <select className="input" value={client} onChange={(e) => setClient(e.target.value)} style={{ width: 200 }} aria-label="Filter by client">
          <option value="">All clients</option>
          <option value="internal">Internal</option>
          {(customers ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <label className="row" style={{ gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={onlyReporting} onChange={(e) => setOnlyReporting(e.target.checked)} />
          Only resources that report vitals
        </label>
      </div>

      <div className="card">
        {loading && !data ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={8} cols={6} />
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : shown.length === 0 ? (
          <EmptyState icon={<Activity size={20} />} title="No vitals yet" text="Resources appear here once their collector reports CPU, memory or disk." />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {head('name', 'Resource', false)}
                  {head('client', 'Client', false)}
                  <th style={{ width: 140 }}>Kind</th>
                  <th style={{ width: 110 }}>Health</th>
                  {head('cpu', 'CPU')}
                  {head('memory', 'Memory')}
                  {head('disk', 'Disk (worst)')}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const stale = r.ts === null || now - r.ts > STALE_MS;
                  return (
                    <tr key={r.id}>
                      <td>
                        <Link to={`/resources/${r.id}`}>
                          <ReachDot health={r.health} />
                          {r.name}
                        </Link>
                      </td>
                      <td className="truncate" style={{ maxWidth: 180, fontSize: 12.5 }}>
                        {clientOf(r) || <span className="subtle">Internal</span>}
                        {r.siteId && siteName.get(r.siteId) && (
                          <div className="subtle" style={{ fontSize: 11.5, marginTop: 2 }}>
                            {siteName.get(r.siteId)}
                          </div>
                        )}
                      </td>
                      <td>{r.kind}</td>
                      <td>
                        <Health health={r.health} />
                      </td>
                      <td><Cell value={r.cpu} stale={stale} /></td>
                      <td><Cell value={r.memory} stale={stale} /></td>
                      <td><Cell value={r.disk} stale={stale} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
